import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import HearingRoundedIcon from "@mui/icons-material/HearingRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useAuth } from "../../hooks/AuthProvider";
import { LanguageContext } from "../../context/LanguageContext";
import {
  BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT,
  fetchVoiceCommandConfig,
} from "../../services/aiVoiceAssistantApi";
import { getAuthToken } from "../../api/httpClient";
import { AI_RAG_CHAT_VOICE_LIVE_URL } from "../../config/env";
import {
  getActiveAiDeviceId,
  subscribeActiveAiDeviceId,
} from "../../utils/aiDeviceContext";
import { useBrowserVoiceCommandSettings } from "../../context/BrowserVoiceCommandContext";
import headerData from "../../languages/header.json";
import LiveVoiceClient from "./LiveVoiceClient";
import "./VoiceAssistant.css";

const CONVERSATION_STORAGE_PREFIX = "sama-ai-voice-conversation";
const WAKE_RESTART_DELAY_MS = 500;
const LIVE_REARM_DELAY_MS = 650;

const DEFAULT_LIVE_CONFIG = Object.freeze({
  enabled: true,
  voice: "nova",
  endOfTurnSilenceMs: 2000,
  stopPhrases: [
    "stop sama",
    "end sama",
    "goodbye sama",
    "turn off sama",
    "توقف سما",
    "اوقف سما",
    "أوقف سما",
    "اغلق سما",
    "أغلق سما",
    "انهي سما",
    "أنهي سما",
  ],
});

// Web Speech can transcribe the same spoken wake phrase in many different ways.
// Keep these aliases intentionally broad: they are only used while Hey Sama is in
// standby and a greeting + Sama-like phrase is required before the live session starts.
const HELLO_WORDS = [
  "hello",
  "helo",
  "hallo",
  "hullo",
  "hillo",
  "holla",
  "holly",
  "hollow",
  "hallow",
  "halo",
  "hey",
  "hay",
  "heya",
  "heyya",
  "heyy",
  "hey there",
  "hi",
  "hai",
  "hy",
  "high",
  "hie",
  "hiya",
  "hiya there",
  "hi there",
  "hello there",
  "hello hi",
  "hey hi",
  "hey hello",
  "hi hello",
  // Existing Arabic greeting variants remain supported.
  "مرحبا",
  "هلا",
  "اهلا",
  "أهلا",
  "هاي",
];

const SAMA_WORDS = [
  "sama",
  "samaa",
  "samma",
  "samar",
  "samah",
  "samaah",
  "samantha",
  "sam",
  "samo",
  "samu",
  "samuel",
  "simo",
  "simon",
  "simone",
  "sam a",
  "some a",
  "soma",
  "somer",
  "summer",
  "someone",
  "somehow",
  "sema",
  "semma",
  "semih",
  "semmel",
  "simmel",
  "samae",
  "semantic",
  "semantics",
  "semitic",
  "somebody",
  "sama ai",
  "sama iot",
  "sama system",
  "sir",
  "isomil",
  "sumo",
  "semant",
  "isomer",
  // Existing Arabic Sama variants remain supported.
  "سما",
  "ساما",
];

const normalizeChatSessionId = (value) => {
  const clean = String(value || "").trim();
  return /^[A-Za-z0-9_.:-]{1,128}$/.test(clean) ? clean : "";
};

const normalizeSpeechText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[،؟?!.,;:]/g, " ")
    .replace(/[^\p{L}\p{N}\s_-]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const getStorageKey = (userId, organizationId) =>
  `${CONVERSATION_STORAGE_PREFIX}:${userId || "anonymous"}:${organizationId || "no-organization"}`;

const readSessionId = (key) => {
  if (typeof window === "undefined" || !key) return "";
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return "";
    const value = JSON.parse(raw);
    return normalizeChatSessionId(value?.sessionId || value?.chatSessionId);
  } catch {
    return "";
  }
};

const saveSessionId = (key, sessionId) => {
  if (typeof window === "undefined" || !key || !sessionId) return;
  try {
    const raw = window.sessionStorage.getItem(key);
    const current = raw ? JSON.parse(raw) : {};
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ ...current, sessionId: normalizeChatSessionId(sessionId) })
    );
  } catch (error) {
    console.error("Failed to save live voice session:", error);
  }
};

const getLiveConfig = (voiceConfig) => ({
  ...DEFAULT_LIVE_CONFIG,
  ...(voiceConfig?.liveConversation || {}),
  stopPhrases:
    Array.isArray(voiceConfig?.liveConversation?.stopPhrases) &&
    voiceConfig.liveConversation.stopPhrases.length
      ? voiceConfig.liveConversation.stopPhrases
      : DEFAULT_LIVE_CONFIG.stopPhrases,
});

const STATIC_WAKE_PHRASES = (() => {
  const phrases = new Set();
  HELLO_WORDS.forEach((hello) => {
    SAMA_WORDS.forEach((sama) => {
      const phrase = normalizeSpeechText(`${hello} ${sama}`);
      if (phrase) phrases.add(phrase);
    });
  });
  return Object.freeze(Array.from(phrases));
})();

let wakePhraseCacheKey = "";
let wakePhraseCache = STATIC_WAKE_PHRASES;

const getWakePhrases = (wakeWords = []) => {
  const configuredWakeWords = (Array.isArray(wakeWords) && wakeWords.length ? wakeWords : ["sama"])
    .map(normalizeSpeechText)
    .filter(Boolean);
  const cacheKey = configuredWakeWords.join("\u0000");

  if (cacheKey === wakePhraseCacheKey) return wakePhraseCache;

  const phrases = new Set(STATIC_WAKE_PHRASES);

  configuredWakeWords.forEach((wake) => {
    // A configured multi-word value is already an explicit wake phrase.
    if (wake.includes(" ")) phrases.add(wake);

    // A configured bare wake word also benefits from all supported greeting variants.
    HELLO_WORDS.forEach((hello) => {
      const phrase = normalizeSpeechText(`${hello} ${wake}`);
      if (phrase) phrases.add(phrase);
    });
  });

  wakePhraseCacheKey = cacheKey;
  wakePhraseCache = Object.freeze(Array.from(phrases));
  return wakePhraseCache;
};

const matchesWakePhrase = (transcript, wakeWords) => {
  const normalized = normalizeSpeechText(transcript);
  if (!normalized) return false;
  return getWakePhrases(wakeWords).some(
    (phrase) =>
      normalized === phrase ||
      normalized.startsWith(`${phrase} `) ||
      normalized.endsWith(` ${phrase}`)
  );
};

const matchesStopPhrase = (transcript, stopPhrases) => {
  const normalized = normalizeSpeechText(transcript);
  if (!normalized) return false;
  return (stopPhrases || []).some((phrase) => normalizeSpeechText(phrase) === normalized);
};

const hasSpeechText = (value) => {
  if (typeof value !== "string") return false;
  const clean = value.replace(/[\p{Cf}\p{Cc}ـ]/gu, "").trim();
  if (/^(?:\[(?:noise|background noise|silence|blank_audio|music|ضجيج|صمت|موسيقى)\]|\((?:noise|background noise|silence|blank_audio|music|ضجيج|صمت|موسيقى)\))$/iu.test(clean)) {
    return false;
  }
  return /[\p{L}\p{N}]/u.test(clean);
};

const getTranscriptItemId = (event) => String(event?.item_id || event?.id_item || "").trim();

const updateTranscriptMessages = (messages, event) => {
  const itemId = getTranscriptItemId(event);
  if (!itemId) return messages;
  const id = `user-${itemId}`;

  if (event.type === "transcript.ignored" || event.type === "transcript.error") {
    return messages.filter((message) => message.id !== id);
  }

  const existing = messages.find((message) => message.id === id);
  const partial = event.type === "transcript.delta";
  const delta = String(event.delta ?? event.text ?? "");
  const text = partial ? `${existing?.text || ""}${delta}` : String(event.text || "");

  if (!hasSpeechText(text)) {
    return partial ? messages : messages.filter((message) => message.id !== id);
  }

  const next = { id, role: "user", text, partial, sources: [] };
  const updated = existing
    ? messages.map((message) => (message.id === id ? next : message))
    : [...messages, next];
  return updated.slice(-12);
};

const readableSources = (sources) =>
  (Array.isArray(sources) ? sources : [])
    .map((source) => {
      if (typeof source === "string") return source.trim();
      if (!source || typeof source !== "object") return "";
      return String(source.title || source.name || source.source || source.url || source.id || "").trim();
    })
    .filter(Boolean)
    .slice(0, 4);

const getWorkletUrl = () => {
  const base = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/sama-live-voice-worklet.js`;
};

const getRecognitionLanguage = (config, lang) => {
  const languages = Array.isArray(config?.languages) ? config.languages : [];
  const prefix = lang === "ar" ? "ar" : lang === "ur" ? "ur" : "en";
  return (
    languages.find((item) => String(item || "").toLowerCase().startsWith(prefix)) ||
    languages[0] ||
    (lang === "ar" ? "ar-SA" : lang === "ur" ? "ur-PK" : "en-US")
  );
};

const stateLabel = (state, lang) => {
  const labels = headerData[lang]?.header || headerData.en?.header || {};
  const map = {
    standby: labels.heySamaStandby,
    connecting: labels.heySamaConnecting,
    reconnecting: labels.heySamaReconnecting || labels.heySamaConnecting,
    listening: labels.heySamaListening,
    hearing: labels.heySamaHearing,
    thinking: labels.heySamaThinking,
    speaking: labels.heySamaSpeaking,
    error: labels.heySamaError,
  };
  return map[state] || "";
};

export default function VoiceAssistant() {
  const { org, user, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);
  const {
    settings,
    setEnabled,
    setRuntime,
    registerController,
  } = useBrowserVoiceCommandSettings();

  const [config, setConfig] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [displayState, setDisplayState] = useState("standby");
  const [displayMessage, setDisplayMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [notice, setNotice] = useState("");

  const enabledRef = useRef(settings.enabled);
  const configRef = useRef(config);
  const langRef = useRef(lang);
  const orgRef = useRef(org);
  const userRef = useRef(user);
  const liveClientRef = useRef(null);
  const recognitionRef = useRef(null);
  const recognitionRunningRef = useRef(false);
  const wakeRestartTimerRef = useRef(null);
  const liveRearmTimerRef = useRef(null);
  const preparedAudioContextRef = useRef(null);
  const startLiveRef = useRef(null);
  const startWakeRef = useRef(null);
  const sessionIdRef = useRef("");
  const stoppingRef = useRef(false);
  const scopeKeyRef = useRef("");
  const overlayDismissedRef = useRef(false);

  const canUseVoice = Boolean(permissions?.ai_voice_command);
  const organizationId = org?.id;
  const storageKey = useMemo(
    () => getStorageKey(user?.id, organizationId),
    [organizationId, user?.id]
  );

  useEffect(() => {
    enabledRef.current = settings.enabled;
  }, [settings.enabled]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  useEffect(() => {
    orgRef.current = org;
  }, [org]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() =>
    subscribeActiveAiDeviceId((deviceId) => {
      // A live WebSocket session binds device_id in session.start. If the user
      // navigates to another device (or leaves Device Details), refresh only the
      // transport context while keeping the microphone/live conversation armed.
      liveClientRef.current?.setDeviceId?.(deviceId);
    }),
  []);

  useEffect(() => {
    const scopeChanged = Boolean(scopeKeyRef.current && scopeKeyRef.current !== storageKey);
    scopeKeyRef.current = storageKey;
    sessionIdRef.current = readSessionId(storageKey);

    if (scopeChanged) {
      setMessages([]);
      setNotice("");
      setDisplayMessage("");
      try {
        liveClientRef.current?.end();
      } catch {}
      liveClientRef.current = null;
    }
  }, [storageKey]);

  const updateRuntime = useCallback(
    (state, patch = {}) => {
      const live = ["connecting", "reconnecting", "listening", "hearing", "thinking", "speaking"].includes(state);
      setDisplayState(state);
      if (Object.prototype.hasOwnProperty.call(patch, "message")) {
        setDisplayMessage(patch.message || "");
      }
      setRuntime({
        state,
        live,
        ...patch,
      });
    },
    [setRuntime]
  );

  const stopWakeRecognition = useCallback(() => {
    window.clearTimeout(wakeRestartTimerRef.current);
    wakeRestartTimerRef.current = null;

    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    recognitionRunningRef.current = false;

    if (recognition) {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {}
      try {
        recognition.stop();
      } catch {}
    }
  }, []);

  const closePreparedAudioContext = useCallback(() => {
    const context = preparedAudioContextRef.current;
    preparedAudioContextRef.current = null;
    if (context && context.state !== "closed") {
      context.close().catch(() => {});
    }
  }, []);

  const prepareAudio = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Microphone access is not supported in this browser.");
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      throw new Error("Hey Sama wake-word detection requires Chrome or Edge with Web Speech support.");
    }

    let context = preparedAudioContextRef.current;
    if (!context || context.state === "closed") {
      context = new AudioContext({ sampleRate: 24000, latencyHint: "interactive" });
      preparedAudioContextRef.current = context;
    }
    if (context.state !== "running") await context.resume();

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  }, []);

  const disableAssistant = useCallback(
    ({ persist = false } = {}) => {
      stoppingRef.current = true;
      enabledRef.current = false;
      window.clearTimeout(liveRearmTimerRef.current);
      liveRearmTimerRef.current = null;
      stopWakeRecognition();

      const client = liveClientRef.current;
      liveClientRef.current = null;
      try {
        client?.end();
      } catch {}

      closePreparedAudioContext();
      overlayDismissedRef.current = false;
      setOverlayVisible(false);
      setDisplayMessage("");
      setMessages([]);
      setNotice("");
      updateRuntime("off", { live: false, level: 0, error: "", message: "" });
      if (persist) setEnabled(false);

      window.setTimeout(() => {
        stoppingRef.current = false;
      }, 100);
    },
    [closePreparedAudioContext, setEnabled, stopWakeRecognition, updateRuntime]
  );

  const handleLiveError = useCallback(
    (message) => {
      const clean = String(message || "Live voice is unavailable.");
      updateRuntime("error", { error: clean, message: clean, live: false, level: 0 });
      setDisplayMessage(clean);
      setNotice(clean);
      overlayDismissedRef.current = false;
      setOverlayVisible(true);
    },
    [updateRuntime]
  );

  const handleLiveNotice = useCallback((message) => {
    const clean = String(message || "").trim();
    if (!clean) return;
    setNotice(clean);
    if (!overlayDismissedRef.current) setOverlayVisible(true);
  }, []);

  const returnToWakeStandby = useCallback(() => {
    liveClientRef.current = null;
    if (!enabledRef.current || stoppingRef.current) return;

    window.clearTimeout(liveRearmTimerRef.current);
    liveRearmTimerRef.current = window.setTimeout(() => {
      if (!enabledRef.current || liveClientRef.current || stoppingRef.current) return;
      updateRuntime("standby", { live: false, level: 0, error: "", message: "" });
      if (!overlayDismissedRef.current) setOverlayVisible(true);
      startWakeRef.current?.();
    }, LIVE_REARM_DELAY_MS);
  }, [updateRuntime]);

  const handleLiveEvent = useCallback(
    (event) => {
      if (!event || typeof event !== "object") return;

      if (event.type === "session.ready") {
        const nextSessionId = normalizeChatSessionId(event.session_id) || sessionIdRef.current;
        if (nextSessionId) {
          sessionIdRef.current = nextSessionId;
          saveSessionId(getStorageKey(userRef.current?.id, orgRef.current?.id), nextSessionId);
        }
        setDisplayMessage("");
        setNotice("");
        return;
      }

      if (["transcript.delta", "transcript.final", "transcript.ignored", "transcript.error"].includes(event.type)) {
        setMessages((previous) => updateTranscriptMessages(previous, event));

        if (event.type === "transcript.delta") {
          const delta = String(event.delta ?? event.text ?? "").trim();
          if (delta) setDisplayMessage(delta);
          return;
        }

        if (event.type === "transcript.final") {
          const text = String(event.text || "").trim();
          setDisplayMessage(hasSpeechText(text) ? text : "");
          const liveConfig = getLiveConfig(configRef.current);
          if (matchesStopPhrase(text, liveConfig.stopPhrases)) {
            disableAssistant({ persist: true });
          }
          return;
        }

        setDisplayMessage("");
        return;
      }

      if (event.type === "response.text") {
        const text = String(event.text || "").trim();
        const responseId = String(event.response_id || "unknown");
        if (text) {
          setDisplayMessage(text);
          setMessages((previous) => {
            const id = `assistant-${responseId}`;
            const next = {
              id,
              role: "assistant",
              text,
              partial: false,
              sources: readableSources(event.sources),
            };
            const exists = previous.some((message) => message.id === id);
            const updated = exists
              ? previous.map((message) => (message.id === id ? next : message))
              : [...previous, next];
            return updated.slice(-12);
          });
        }
        return;
      }

      if (event.type === "turn.queued") {
        setNotice(
          langRef.current === "ar"
            ? "تم سماع سؤالك التالي وسيتم الرد بعد انتهاء الرد الحالي."
            : langRef.current === "ur"
              ? "آپ کا اگلا سوال سن لیا گیا ہے اور موجودہ جواب کے بعد اس کا جواب دیا جائے گا۔"
              : "Your next question is queued until the current reply finishes."
        );
        return;
      }

      if (event.type === "response.started") {
        setNotice("");
        return;
      }

      if (event.type === "response.cancelled") {
        setNotice(
          langRef.current === "ar"
            ? "تم إيقاف الرد. الميكروفون ما زال يستمع."
            : langRef.current === "ur"
              ? "جواب روک دیا گیا ہے۔ مائیکروفون اب بھی سن رہا ہے۔"
              : "Reply stopped. The microphone is still listening."
        );
        return;
      }

      // The transport client owns unexpected session/socket recovery. Do not
      // fall back to wake-word standby here: once Hey Sama is activated, the
      // live microphone session remains armed until the user explicitly stops it.
      if (event.type === "session.closed") return;
    },
    [disableAssistant, returnToWakeStandby]
  );

  const startLiveConversation = useCallback(async () => {
    if (!enabledRef.current || liveClientRef.current || stoppingRef.current) return;

    const voiceConfig = configRef.current;
    const liveConfig = getLiveConfig(voiceConfig);
    if (!voiceConfig?.enabled || liveConfig.enabled === false) return;

    const token = getAuthToken();
    const currentOrgId = Number(orgRef.current?.id);
    if (!token || !Number.isSafeInteger(currentOrgId) || currentOrgId <= 0) {
      handleLiveError("The authenticated user or organization is unavailable.");
      return;
    }

    if (!AI_RAG_CHAT_VOICE_LIVE_URL) {
      handleLiveError("The live voice WebSocket URL is not configured.");
      return;
    }

    stopWakeRecognition();
    overlayDismissedRef.current = false;
    setOverlayVisible(true);
    setDisplayMessage("");
    setMessages([]);
    setNotice("");
    updateRuntime("connecting", { error: "", message: "", level: 0, live: true });

    try {
      let context = preparedAudioContextRef.current;
      if (!context || context.state === "closed") {
        context = new AudioContext({ sampleRate: 24000, latencyHint: "interactive" });
        preparedAudioContextRef.current = context;
      }
      if (context.state !== "running") await context.resume();

      const client = new LiveVoiceClient({
        workletUrl: getWorkletUrl(),
        onEvent: handleLiveEvent,
        onState: (state) => {
          if (state === "idle") {
            if (liveClientRef.current === client) liveClientRef.current = null;
            if (enabledRef.current && !stoppingRef.current) returnToWakeStandby();
            return;
          }
          if (!overlayDismissedRef.current) setOverlayVisible(true);
          updateRuntime(state, { error: "", live: true });
        },
        onLevel: (level) => setRuntime({ level: Math.max(0, Number(level) || 0) }),
        onError: handleLiveError,
        onNotice: handleLiveNotice,
      });

      liveClientRef.current = client;
      await client.start({
        url: AI_RAG_CHAT_VOICE_LIVE_URL,
        token,
        getToken: getAuthToken,
        organizationId: currentOrgId,
        sessionId: sessionIdRef.current,
        deviceId: getActiveAiDeviceId(),
        voice: liveConfig.voice || "nova",
        endOfTurnSilenceMs: liveConfig.endOfTurnSilenceMs,
        audioContext: context,
      });
    } catch (error) {
      liveClientRef.current = null;
      handleLiveError(error?.message || "Unable to start live voice.");
      returnToWakeStandby();
    }
  }, [handleLiveError, handleLiveEvent, handleLiveNotice, returnToWakeStandby, setRuntime, stopWakeRecognition, updateRuntime]);

  useEffect(() => {
    startLiveRef.current = startLiveConversation;
  }, [startLiveConversation]);

  const startWakeRecognition = useCallback(() => {
    if (
      !enabledRef.current ||
      liveClientRef.current ||
      recognitionRunningRef.current ||
      stoppingRef.current
    ) {
      return;
    }

    const voiceConfig = configRef.current;
    const liveConfig = getLiveConfig(voiceConfig);
    if (!canUseVoice || !voiceConfig?.enabled || liveConfig.enabled === false) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      handleLiveError("Hey Sama wake-word detection requires Chrome or Edge with Web Speech support.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = getRecognitionLanguage(voiceConfig, langRef.current);
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      recognitionRunningRef.current = true;
      updateRuntime("standby", { live: false, error: "", message: "", level: 0 });
    };

    recognition.onresult = (event) => {
      if (!enabledRef.current || liveClientRef.current || stoppingRef.current) return;

      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const part = event.results[i]?.[0]?.transcript;
        if (part) transcript += ` ${part}`;
      }

      const clean = transcript.trim();
      if (!clean) return;

      if (matchesWakePhrase(clean, configRef.current?.wakeWords)) {
        stopWakeRecognition();
        startLiveRef.current?.();
      }
    };

    recognition.onerror = (event) => {
      recognitionRunningRef.current = false;
      if (!enabledRef.current || stoppingRef.current) return;

      if (["not-allowed", "service-not-allowed"].includes(event.error)) {
        handleLiveError("Microphone access is blocked. Allow microphone access to use Hey Sama.");
        setEnabled(false);
        enabledRef.current = false;
        return;
      }

      if (event.error === "aborted") return;
      window.clearTimeout(wakeRestartTimerRef.current);
      wakeRestartTimerRef.current = window.setTimeout(
        () => startWakeRef.current?.(),
        WAKE_RESTART_DELAY_MS
      );
    };

    recognition.onend = () => {
      recognitionRunningRef.current = false;
      if (recognitionRef.current === recognition) recognitionRef.current = null;
      if (!enabledRef.current || liveClientRef.current || stoppingRef.current) return;

      window.clearTimeout(wakeRestartTimerRef.current);
      wakeRestartTimerRef.current = window.setTimeout(
        () => startWakeRef.current?.(),
        WAKE_RESTART_DELAY_MS
      );
    };

    try {
      recognition.start();
    } catch (error) {
      recognitionRef.current = null;
      recognitionRunningRef.current = false;
      handleLiveError(error?.message || "Unable to start Hey Sama listening.");
    }
  }, [canUseVoice, handleLiveError, setEnabled, stopWakeRecognition, updateRuntime]);

  useEffect(() => {
    startWakeRef.current = startWakeRecognition;
  }, [startWakeRecognition]);

  useEffect(() => {
    let ignore = false;
    if (!canUseVoice || !organizationId) {
      setConfig(null);
      return undefined;
    }

    fetchVoiceCommandConfig(organizationId)
      .then((next) => {
        if (!ignore) setConfig(next);
      })
      .catch((error) => {
        console.error("Failed to load Hey Sama configuration:", error);
        if (!ignore) handleLiveError("Unable to load Hey Sama configuration.");
      });

    return () => {
      ignore = true;
    };
  }, [canUseVoice, handleLiveError, organizationId]);

  useEffect(() => {
    const handleConfigUpdated = (event) => {
      const nextConfig = event?.detail || null;
      setConfig(nextConfig);
      if (nextConfig && liveClientRef.current) {
        const nextLiveConfig = getLiveConfig(nextConfig);
        liveClientRef.current.setEndOfTurnSilenceMs?.(nextLiveConfig.endOfTurnSilenceMs);
      }
    };
    window.addEventListener(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, handleConfigUpdated);
    return () => {
      window.removeEventListener(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, handleConfigUpdated);
    };
  }, []);

  useEffect(() => {
    const unregister = registerController({
      enable: async () => {
        if (!canUseVoice) throw new Error("You do not have permission to use Hey Sama.");
        const liveConfig = getLiveConfig(configRef.current);
        if (configRef.current && (!configRef.current.enabled || liveConfig.enabled === false)) {
          throw new Error("Hey Sama live conversation is disabled by system configuration.");
        }
        await prepareAudio();
        enabledRef.current = true;
        window.setTimeout(() => startWakeRef.current?.(), 0);
        return true;
      },
      disable: () => disableAssistant({ persist: false }),
      stopReply: () => liveClientRef.current?.stopReply(),
    });

    return unregister;
  }, [canUseVoice, disableAssistant, prepareAudio, registerController]);

  useEffect(() => {
    enabledRef.current = settings.enabled;

    if (!settings.enabled) {
      disableAssistant({ persist: false });
      return;
    }

    if (!canUseVoice) {
      disableAssistant({ persist: true });
      return;
    }

    if (!config) {
      stopWakeRecognition();
      updateRuntime("standby", { live: false, level: 0, error: "", message: "" });
      return;
    }

    const liveConfig = getLiveConfig(config);
    if (!config.enabled || liveConfig.enabled === false) {
      disableAssistant({ persist: true });
      return;
    }

    startWakeRecognition();
  }, [canUseVoice, config, disableAssistant, settings.enabled, startWakeRecognition, stopWakeRecognition, updateRuntime]);

  useEffect(() => {
    return () => {
      enabledRef.current = false;
      window.clearTimeout(wakeRestartTimerRef.current);
      window.clearTimeout(liveRearmTimerRef.current);
      stopWakeRecognition();
      try {
        liveClientRef.current?.end();
      } catch {}
      liveClientRef.current = null;
      closePreparedAudioContext();
    };
  }, [closePreparedAudioContext, stopWakeRecognition]);

  if (!canUseVoice || !settings.enabled || !config?.enabled || getLiveConfig(config).enabled === false) {
    return null;
  }

  const live = ["connecting", "reconnecting", "listening", "hearing", "thinking", "speaking"].includes(displayState);
  const showPanel = overlayVisible && (live || displayState === "error" || displayState === "standby");
  if (!showPanel) return null;

  return (
    <div className="browser-vc-overlay sama-live-overlay">
      <section className={`browser-vc-card sama-live-card is-${displayState}`} aria-live="polite">
        <button
          type="button"
          className="browser-vc-close"
          onClick={() => {
            overlayDismissedRef.current = true;
            setOverlayVisible(false);
          }}
          aria-label={lang === "ar" ? "إخفاء حالة المحادثة الصوتية" : lang === "ur" ? "لائیو وائس اسٹیٹس چھپائیں" : "Hide live voice status"}
          title={lang === "ar" ? "إخفاء الحالة" : lang === "ur" ? "اسٹیٹس چھپائیں" : "Hide status"}
        >
          <CloseRoundedIcon />
        </button>

        <div className="browser-vc-orb sama-live-orb" aria-hidden="true">
          {displayState === "thinking" || displayState === "connecting" ? (
            <PsychologyRoundedIcon />
          ) : displayState === "speaking" ? (
            <GraphicEqRoundedIcon />
          ) : (
            <HearingRoundedIcon />
          )}
        </div>

        <strong>{stateLabel(displayState, lang)}</strong>

        {messages.length ? (
          <div className="sama-live-transcript" role="log" aria-live="polite">
            {messages.slice(-4).map((message) => (
              <div key={message.id} className={`sama-live-bubble is-${message.role}${message.partial ? " is-partial" : ""}`}>
                <span>{message.role === "user"
                  ? (lang === "ar" ? "أنت" : lang === "ur" ? "آپ" : "You")
                  : "Sama"}</span>
                <p dir="auto">{message.text}</p>
                {message.sources?.length ? (
                  <small>{message.sources.join(" · ")}</small>
                ) : null}
              </div>
            ))}
          </div>
        ) : displayMessage ? (
          <p className="sama-live-message" dir="auto">{displayMessage}</p>
        ) : null}

        {notice ? <p className="sama-live-notice" role="status" dir="auto">{notice}</p> : null}

        <div className="sama-live-actions">
          {displayState === "speaking" ? (
            <button type="button" onClick={() => liveClientRef.current?.stopReply()}>
              <StopCircleOutlinedIcon fontSize="small" />
              {lang === "ar" ? "إيقاف الرد" : lang === "ur" ? "جواب روکیں" : "Stop reply"}
            </button>
          ) : null}
          <button type="button" className="danger" onClick={() => disableAssistant({ persist: true })}>
            {lang === "ar" ? "إيقاف Hey Sama" : lang === "ur" ? "Hey Sama بند کریں" : "Turn off Hey Sama"}
          </button>
        </div>
      </section>
    </div>
  );
}
